import {
  describeDiffState,
  diffEmptyCopy,
  versionLabel,
  type DiffPair,
  type FieldDiff,
} from "@/lib/blog/history-model";
import { LTR_ISOLATE_ATTRS } from "@/lib/blog/locale-options";

interface RevisionDiffProps {
  pair: DiffPair;
  revisionCount: number;
  diffs: FieldDiff[];
  loading?: boolean;
}

/**
 * Published → current, by default (Q28).
 *
 * When the two are the same there is nothing waiting to ship, and the panel
 * says exactly that rather than rendering an empty two-column shell — a dead
 * segment reads as a broken diff, not as good news.
 */
export function RevisionDiff({ pair, revisionCount, diffs, loading }: RevisionDiffProps) {
  const state = describeDiffState(pair, revisionCount);

  if (loading === true) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="revision-diff-loading">
        Reading both revisions…
      </p>
    );
  }

  if (state.kind !== "compare" && state.kind !== "never-published") {
    return (
      <p className="text-sm text-muted-foreground" data-testid="revision-diff-empty">
        {diffEmptyCopy(state)}
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="revision-diff">
      <p className="text-sm text-muted-foreground">
        {state.kind === "never-published"
          ? diffEmptyCopy(state)
          : `Comparing ${versionLabel(state.from)} → ${versionLabel(state.to)}`}
      </p>
      {diffs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No field changed between these revisions.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th scope="col" className="p-2">
                Field
              </th>
              <th scope="col" className="p-2">
                Before
              </th>
              <th scope="col" className="p-2">
                After
              </th>
            </tr>
          </thead>
          <tbody>
            {diffs.map((diff) => (
              <tr key={diff.path} className="border-b align-top">
                <th scope="row" className="p-2 text-left font-normal">
                  <span className="block">{diff.label}</span>
                  <span
                    className="block font-mono text-xs text-muted-foreground"
                    {...LTR_ISOLATE_ATTRS}
                  >
                    {diff.path}
                  </span>
                </th>
                <td className="p-2 text-muted-foreground">{diff.before}</td>
                <td className="p-2">{diff.after}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
